/**
 * widgets/CommandPalette.ts · 全局 Cmd+K 命令面板 · 复用 v200 TN_KEYBINDS
 *
 * 用法:
 *   - ⌘K / Ctrl+K 打开
 *   - 输入关键字 fuzzy 搜 (ZH/VI/英文 icon)
 *   - ↑ ↓ 选, Enter 跳, Esc 关
 *   - 点击空白处关
 *
 * 复用 widgets/Sidebar.ts PAGES 字典 (单一事实源)
 * RBAC 过滤 · 仅显当前角色可见 page
 */

import { PAGES } from './Sidebar';
import { getCurrentUser } from '@core/auth';
import { getRolePages } from '@core/rbac';
import { query } from '@core/db';

type Item = { key: string; label: string; icon: string; section: string; sub?: string; hash?: string };

let _open = false;
let _selectedIdx = 0;
let _filtered: Item[] = [];

const OVERLAY_ID = 'tn-cmdk-overlay';
const INPUT_ID = 'tn-cmdk-input';
const LIST_ID = 'tn-cmdk-list';

export function initCommandPalette(): void {
  // 全局 ⌘K / Ctrl+K 监听 · capture 优先级
  document.addEventListener('keydown', (e) => {
    // 不拦 input/textarea 内 (用户正在输入)
    const target = e.target as HTMLElement;
    const inEditable = target && (
      target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    );

    // ⌘K / Ctrl+K → 打开 (不论焦点在哪, 让用户跨页搜索)
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      e.stopPropagation();
      open();
      return;
    }

    // Esc → 关 (palette 打开时)
    if (e.key === 'Escape' && _open) {
      e.preventDefault();
      close();
      return;
    }

    // palette 已打开 · 处理导航
    if (_open && !inEditable) return;  // 输入框内不拦其他键
  }, true);

  // 顶 NAV 的 ⌘K 按钮也调 open()
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t && t.closest && t.closest('#tn-cmdk-btn')) {
      e.preventDefault();
      open();
    }
  });
}

function open(): void {
  if (_open) return;
  _open = true;
  _selectedIdx = 0;

  // 构建 overlay + modal
  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    z-index: 99998;
    display: flex; align-items: flex-start; justify-content: center;
    padding-top: 12vh;
  `;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  const modal = document.createElement('div');
  modal.style.cssText = `
    background: #fff; border-radius: 12px;
    width: 92%; max-width: 600px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    display: flex; flex-direction: column;
    max-height: 70vh;
  `;
  modal.innerHTML = `
    <div style="padding: 14px 18px; border-bottom: 1px solid #e5e7eb;">
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 18px;">🔍</span>
        <input id="${INPUT_ID}" type="text" autocomplete="off" spellcheck="false"
          placeholder="搜索页面 · 客户 · 工单 ... (↑ ↓ 选, Enter 跳, Esc 关)"
          style="flex: 1; border: 0; outline: 0; font-size: 16px; padding: 4px;" />
        <span style="font-size: 11px; color: #9ca3af; background: #f3f4f6; padding: 2px 6px; border-radius: 4px;">⌘K</span>
      </div>
    </div>
    <div id="${LIST_ID}" style="overflow-y: auto; max-height: calc(70vh - 64px); padding: 6px;"></div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  const input = modal.querySelector<HTMLInputElement>(`#${INPUT_ID}`)!;
  input.addEventListener('input', () => updateList(input.value));
  input.addEventListener('keydown', handleInputKey);
  setTimeout(() => input.focus(), 50);

  // 初始 list
  updateList('');
}

function close(): void {
  if (!_open) return;
  _open = false;
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) overlay.remove();
}

function handleInputKey(e: KeyboardEvent): void {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    _selectedIdx = Math.min(_selectedIdx + 1, _filtered.length - 1);
    renderList();
    return;
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    _selectedIdx = Math.max(_selectedIdx - 1, 0);
    renderList();
    return;
  }
  if (e.key === 'Enter') {
    e.preventDefault();
    const sel = _filtered[_selectedIdx];
    if (sel) {
      close();
      window.location.hash = '#' + (sel.hash || sel.key);
    }
    return;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    close();
    return;
  }
}

function updateList(qStr: string): void {
  const user = getCurrentUser();
  const allowed = user ? getRolePages(user.role) : Object.keys(PAGES);

  const q = qStr.trim().toLowerCase();
  const lang = document.documentElement.lang.startsWith('vi') ? 'vi' : 'zh';

  const pageItems: Item[] = allowed
    .map((key) => {
      const meta = PAGES[key];
      if (!meta) return null;
      const label = lang === 'vi' ? meta.vi : meta.zh;
      return { key, label, icon: meta.icon, section: meta.section, hash: key };
    })
    .filter(Boolean) as Item[];

  // Fuzzy 页面
  const filteredPages = q
    ? pageItems.filter(i =>
        i.key.toLowerCase().includes(q) ||
        i.label.toLowerCase().includes(q) ||
        i.section.toLowerCase().includes(q)
      )
    : pageItems;

  // v339 跨表深度搜索 (q ≥ 2 字符触发)
  let deepResults: Item[] = [];
  if (q.length >= 2) {
    try {
      deepResults = deepSearch(q);
    } catch { /* db not ready · ignore */ }
  }

  _filtered = [...deepResults, ...filteredPages];
  _selectedIdx = 0;
  renderList();
}

/** v339 跨表深度搜索 · customer / SO / WO / quotation / material */
function deepSearch(q: string): Item[] {
  const results: Item[] = [];
  const pattern = `%${q}%`;
  try {
    const sos = query<{ id: number; code: string; name: string }>(
      `SELECT so.id, so.code, COALESCE(c.name,'-') AS name FROM sales_orders so LEFT JOIN customers c ON c.id=so.customer_id WHERE LOWER(COALESCE(so.code,'')) LIKE ? OR LOWER(COALESCE(c.name,'')) LIKE ? LIMIT 5`, [pattern, pattern]);
    sos.forEach(r => results.push({ key: `so-${r.id}`, label: `${r.code} · ${r.name}`, icon: '📋', section: '订单', sub: '销售订单', hash: `order360?id=${r.id}` }));
  } catch {}
  try {
    const cs = query<{ id: number; name: string }>(
      `SELECT id, COALESCE(name, '-') AS name FROM customers WHERE LOWER(COALESCE(name,'')) LIKE ? OR LOWER(COALESCE(code,'')) LIKE ? LIMIT 5`, [pattern, pattern]);
    cs.forEach(r => results.push({ key: `c-${r.id}`, label: r.name, icon: '👥', section: '客户', sub: '客户', hash: `customer360?id=${r.id}` }));
  } catch {}
  try {
    const wos = query<{ id: number; code: string }>(
      `SELECT id, code FROM work_orders WHERE LOWER(COALESCE(code,'')) LIKE ? LIMIT 5`, [pattern]);
    wos.forEach(r => results.push({ key: `wo-${r.id}`, label: r.code, icon: '🏭', section: '工单', sub: '工单', hash: `woDetail?id=${r.id}` }));
  } catch {}
  try {
    const qs = query<{ id: number; code: string }>(
      `SELECT id, code FROM quotations WHERE LOWER(COALESCE(code,'')) LIKE ? LIMIT 5`, [pattern]);
    qs.forEach(r => results.push({ key: `q-${r.id}`, label: r.code, icon: '📝', section: '报价', sub: '报价', hash: `quotationDetail?id=${r.id}` }));
  } catch {}
  try {
    const ms = query<{ id: number; code: string; name: string }>(
      `SELECT id, code, COALESCE(name, '-') AS name FROM raw_materials WHERE LOWER(COALESCE(code,'')) LIKE ? OR LOWER(COALESCE(name,'')) LIKE ? LIMIT 5`, [pattern, pattern]);
    ms.forEach(r => results.push({ key: `m-${r.id}`, label: `${r.code} · ${r.name}`, icon: '📦', section: '物料', sub: '原料', hash: 'rawMaterialQuery' }));
  } catch {}
  return results;
}

function renderList(): void {
  const list = document.getElementById(LIST_ID);
  if (!list) return;
  if (_filtered.length === 0) {
    list.innerHTML = '<div style="padding: 24px; text-align: center; color: #9ca3af;">📭 无匹配</div>';
    return;
  }

  list.innerHTML = _filtered.map((item, idx) => {
    const active = idx === _selectedIdx;
    const subBadge = item.sub ? `<span style="font-size:10px;color:#7c3aed;background:#ede9fe;padding:1px 5px;border-radius:3px;margin-right:4px;">${escapeHtml(item.sub)}</span>` : '';
    return `
      <button data-cmdk-hash="${escapeHtml(item.hash || item.key)}"
        style="
          display: flex; align-items: center; gap: 10px;
          width: 100%; padding: 8px 12px;
          border: 0; background: ${active ? '#eff6ff' : 'transparent'};
          border-radius: 6px; cursor: pointer;
          text-align: left; font-size: 14px;
          ${active ? 'box-shadow: inset 0 0 0 2px #3b82f6;' : ''}
        ">
        <span style="font-size: 18px;">${item.icon}</span>
        <span style="flex: 1;">${escapeHtml(item.label)}</span>
        ${subBadge}<span style="font-size: 11px; color: #9ca3af; background: #f3f4f6; padding: 2px 6px; border-radius: 4px;">${escapeHtml(item.section)}</span>
      </button>
    `;
  }).join('');

  // 行点击
  list.querySelectorAll<HTMLButtonElement>('[data-cmdk-hash]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const hash = btn.getAttribute('data-cmdk-hash');
      if (hash) {
        close();
        window.location.hash = '#' + hash;
      }
    });
  });

  // 自动 scroll active 入视野
  const activeBtn = list.querySelector(`button:nth-child(${_selectedIdx + 1})`);
  if (activeBtn && (activeBtn as HTMLElement).scrollIntoView) {
    (activeBtn as HTMLElement).scrollIntoView({ block: 'nearest' });
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
